import React, { useRef, useState, useEffect } from "react";
import { Button } from "@mui/material";
import { Publish } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { PropTypes } from "prop-types";
import { useTranslation } from "react-i18next";
import {
  trainingObjectStatusIds,
  courseLocationTypes
} from "../../../../constants";
import { isFutureDate } from "../../../../utils/datetime";
import ButtonGroup from "../../../../components/Buttons/ButtonGroup";
import CourseSessionAttendeesModal from "./CourseSessionAttendeesModal";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import WarningModal from "../../../../components/WarningModal/WarningModal";
import {
  updateSessionRoster,
  fetchSessionRoster,
  fetchVirtualClassroomAttendees
} from "../../../../redux/actions/contentCoursesActions";
import { enqueueErrorSnackbar } from "../../../../redux/actions/snackbarActions";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import useLoadingState from "../../../../hooks/useLoadingState";
import useErrorState from "../../../../hooks/useErrorState";

const selector = makeGetEntities();

const CourseSessionRosterButtons = ({ sessionId }) => {
  const { t } = useTranslation();
  const inputRef = useRef();
  const dispatch = useDispatch();
  const isUploadingRoster = useLoadingState("UPDATE_SESSION_ROSTER");
  const isFetchingAttendees = useLoadingState("FETCH_VC_ATTENDEES");
  const fetchAttendeesError = useErrorState("FETCH_VC_ATTENDEES");

  const [attendeesModalOpen, setAttendeesModalOpen] = useState(false);
  const [
    uploadConfirmationModalOpen,
    setUploadConfirmationModalOpen
  ] = useState(false);

  const { sessionData } = useSelector(({ contentCoursesState }) => ({
    sessionData: selector(contentCoursesState, {
      type: "trainingObject",
      id: sessionId
    })
  }));

  const isVirtualClassroom =
    sessionData.iltLocationType === courseLocationTypes.virtualClassroom;

  useEffect(() => {
    if (!isVirtualClassroom) {
      dispatch(fetchSessionRoster(sessionData.id));
    } else {
      dispatch(fetchVirtualClassroomAttendees(sessionId));
    }
  }, []);

  const handleAttendeesModalOpenChange = open => {
    if (!!fetchAttendeesError) {
      dispatch(
        enqueueErrorSnackbar({ message: fetchAttendeesError[0].detail })
      );
    } else {
      setAttendeesModalOpen(open);
    }
  };

  const handleViewRosterClick = () => {
    window.location.href = sessionData.roster.path;
  };

  const handleUploadRoster = newRosterFile => {
    dispatch(updateSessionRoster(sessionData.id, newRosterFile));
  };

  const openFileUploader = () => {
    inputRef.current.click();
    setUploadConfirmationModalOpen(false);
  };

  const handleUploadRosterClick = () => {
    if (!sessionData.roster) {
      openFileUploader();
    } else {
      setUploadConfirmationModalOpen(true);
    }
  };

  return (
    <>
      {isVirtualClassroom ? (
        <Button
          color="primary"
          data-qa="course-session-view-attendees-btn"
          onClick={() => handleAttendeesModalOpenChange(true)}
          disabled={
            isFutureDate(sessionData.startDate) ||
            sessionData.statusTypeId === trainingObjectStatusIds.inactive ||
            isFetchingAttendees
          }
        >
          {t("courseSession.viewAttendeesBtn")}
        </Button>
      ) : (
        <ButtonGroup align="left">
          <Button
            color="primary"
            onClick={() => {
              handleUploadRosterClick();
            }}
            startIcon={
              isUploadingRoster ? (
                <LoadingSpinner size={22} containerHeight="100%" padding={0} />
              ) : (
                <Publish />
              )
            }
            data-qa="course-session-upload-roster-btn"
          >
            {t("courseSession.uploadRosterBtn")}
          </Button>
          <input
            style={{ display: "none" }}
            accept={".pdf, .jpg, .png, .doc, .docx, .xls, .xlsx, .csv, .txt"}
            id="upload-roster"
            type="file"
            ref={inputRef}
            onChange={ev => {
              handleUploadRoster(ev.target.files[0]);
            }}
          />
          <Button
            color="primary"
            onClick={() => handleViewRosterClick()}
            data-qa="course-session-view-roster-btn"
            disabled={!sessionData.roster}
          >
            {t("courseSession.viewRosterBtn")}
          </Button>
        </ButtonGroup>
      )}
      {isVirtualClassroom && (
        <CourseSessionAttendeesModal
          open={attendeesModalOpen}
          onClose={() => handleAttendeesModalOpenChange(false)}
          sessionId={sessionId}
        />
      )}
      <WarningModal
        open={uploadConfirmationModalOpen}
        onClose={() => setUploadConfirmationModalOpen(false)}
        onSubmit={() => openFileUploader()}
        submitButtonText={t("courseSession.uploadBtn")}
        warningMessage={t("courseSession.uploadConfirmationMsg")}
      />
    </>
  );
};

CourseSessionRosterButtons.propTypes = {
  sessionId: PropTypes.string
};

export default CourseSessionRosterButtons;
